import { useCallback, useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { cn } from 'src/helpers/style'

import {
  Skeleton,
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'
import {
  FilterPills,
  FundSummary,
  IFilterPillOption,
  Pagination,
  PdfPreviewDialog,
  SkeletonWrapper,
} from 'src/components/molecules'
import { Table } from 'src/components/organisms'
import {
  Column,
  Data,
  type ITableEmptyState,
} from 'src/components/organisms/table/types'

import { FundUpdatesMobileList } from './components/fund-updates-mobile-list'
import {
  DocumentUpdateCategoryFilter,
  IDocumentUpdateEntity,
  IUseFundUpdatesReturn,
} from './types'

const asDocumentRow = (row: Data): IDocumentUpdateEntity =>
  row as unknown as IDocumentUpdateEntity

interface IFundUpdatesTemplateProps {
  state: IUseFundUpdatesReturn
}

const DocumentsSectionSkeleton = (): JSX.Element => (
  <div className="flex flex-col gap-7">
    <div className="flex flex-wrap gap-4" aria-hidden>
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          className={cn('block h-7 w-[140px] rounded-[12px] animate-skeleton')}
        />
      ))}
    </div>
    <Skeleton.DocumentUpdatesTable rows={6} />
  </div>
)

export const FundUpdatesTemplate = ({
  state,
}: IFundUpdatesTemplateProps): JSX.Element => {
  const { t } = useTranslation()

  const {
    isLoading,
    isError,
    isEmpty,
    categoryFilter,
    categoryOptions,
    isCategoriesLoading,
    isCategoriesError,
    currentPage,
    totalPages,
    pageItems,
    summaryAsOf,
    fundSummaryItems,
    selectedDocument,
    selectedDocumentBlobUrl,
    setCategoryFilter,
    setCurrentPage,
    openDocumentPreview,
    closeDocumentPreview,
  } = state

  const handleDocumentClick = useCallback(
    (document: IDocumentUpdateEntity): void => {
      openDocumentPreview(document)
    },
    [openDocumentPreview],
  )

  const handleRowClick = useCallback(
    (row: Data): void => {
      handleDocumentClick(asDocumentRow(row))
    },
    [handleDocumentClick],
  )

  const hasActiveCategoryFilter = categoryFilter !== 'all'

  const tableEmptyState = useMemo((): ITableEmptyState | undefined => {
    if (!isEmpty) {
      return undefined
    }
    return {
      title: hasActiveCategoryFilter
        ? t('fundUpdates.filters.noResultsFound')
        : t('fundUpdates.filters.emptyTitle'),
      description: hasActiveCategoryFilter
        ? t('fundUpdates.filters.noResultsFoundDescription')
        : t('fundUpdates.filters.emptyDescription'),
    }
  }, [hasActiveCategoryFilter, isEmpty, t])

  const categoryPillOptions = useMemo(
    (): IFilterPillOption<DocumentUpdateCategoryFilter>[] => [
      { value: 'all', label: t('fundUpdates.filters.all') },
      ...categoryOptions.map(category => ({
        value: category,
        label: category,
      })),
    ],
    [categoryOptions, t],
  )

  const documentTableColumns = useMemo(
    (): Column[] => [
      {
        property: 'documentTitle',
        header: t('fundUpdates.table.document'),
        width: '560px',
        renderRowCell: row => {
          const doc = asDocumentRow(row)
          return (
            <td className="pr-6">
              <Typography
                variant={TypographyVariant.title2Xs}
                weight={TypographyWeight.light}
                className="text-content-primary"
              >
                {doc.documentTitle}
              </Typography>
            </td>
          )
        },
      },
      {
        property: 'category',
        header: t('fundUpdates.table.category'),
        width: '310px',
        renderRowCell: row => {
          const doc = asDocumentRow(row)
          return (
            <td className="pr-6">
              <Typography
                variant={TypographyVariant.bodyXs}
                className="text-content-secondary"
              >
                {doc.categoryDisplayLabel}
              </Typography>
            </td>
          )
        },
      },
      {
        property: 'displayDate',
        header: t('fundUpdates.table.date'),
        width: '310px',
        renderRowCell: row => {
          const doc = asDocumentRow(row)
          return (
            <td className="pr-6">
              <Typography
                variant={TypographyVariant.bodyXs}
                className="text-content-secondary"
              >
                {doc.displayDate}
              </Typography>
            </td>
          )
        },
      },
    ],
    [t],
  )

  return (
    <main className="bg-surface-page">
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-10 px-6 py-11 md:px-12 xl:px-0">
        <header className="flex flex-col gap-4">
          <Typography
            variant={TypographyVariant.headline}
            weight={TypographyWeight.light}
            className="text-content-primary"
          >
            {t('fundUpdates.pageTitle')}
          </Typography>
          <div className="h-px w-full bg-content-secondary" />
        </header>

        <div className="flex flex-col gap-[60px]">
          <section className="flex flex-col gap-7">
            <div className="flex flex-col gap-1">
              <Typography
                variant={TypographyVariant.titleSm}
                weight={TypographyWeight.light}
                className="text-content-primary"
              >
                {t('overview.sections.fundSummary')}
              </Typography>
              {summaryAsOf ? (
                <Typography
                  variant={TypographyVariant.bodySm}
                  className="text-content-secondary"
                >
                  {t('fundUpdates.summaryAsOf', { date: summaryAsOf })}
                </Typography>
              ) : null}
            </div>
            <SkeletonWrapper
              isLoading={isLoading}
              skeleton={<Skeleton.SummaryCards count={4} />}
            >
              <FundSummary items={fundSummaryItems} />
            </SkeletonWrapper>
          </section>

          <section className="flex flex-col gap-7">
            <Typography
              variant={TypographyVariant.titleSm}
              weight={TypographyWeight.light}
              className="text-content-primary"
            >
              {t('fundUpdates.documentsSectionTitle')}
            </Typography>

            <SkeletonWrapper
              isLoading={isLoading}
              skeleton={<DocumentsSectionSkeleton />}
            >
              <div className="flex flex-col gap-7">
                <div className="-mx-6 overflow-x-auto px-6 [&::-webkit-scrollbar]:hidden [scrollbar-width:none] md:mx-0 md:overflow-visible md:px-0">
                  {isCategoriesLoading ? (
                    <div
                      role="status"
                      aria-label="Loading categories…"
                      aria-busy="true"
                      className="flex w-max gap-2 md:w-auto md:gap-4"
                    >
                      {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton.Base
                          key={i}
                          className="h-6 w-24 rounded-[12px] opacity-60"
                        />
                      ))}
                    </div>
                  ) : isCategoriesError ? null : (
                    <FilterPills<DocumentUpdateCategoryFilter>
                      ariaLabel={t('fundUpdates.documentCategoriesAria')}
                      value={categoryFilter}
                      options={categoryPillOptions}
                      onChange={setCategoryFilter}
                      className="w-max gap-2 md:w-auto md:gap-4"
                    />
                  )}
                </div>

                {!isError ? (
                  <div className="flex w-full flex-col items-stretch gap-6 md:items-end">
                    <div className="w-full md:hidden">
                      {isEmpty ? (
                        <div className="py-10 text-center">
                          <Typography
                            variant={TypographyVariant.bodyXs}
                            className="text-content-secondary"
                          >
                            {tableEmptyState?.description}
                          </Typography>
                        </div>
                      ) : (
                        <FundUpdatesMobileList
                          documents={pageItems}
                          onDocumentClick={handleDocumentClick}
                        />
                      )}
                    </div>

                    <div className="hidden w-full overflow-x-auto md:block">
                      <Table
                        columns={documentTableColumns}
                        data={pageItems as unknown as Data[]}
                        idPropertyName="id"
                        onRowClick={handleRowClick}
                        emptyState={tableEmptyState}
                      />
                    </div>

                    {!isEmpty ? (
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onChange={setCurrentPage}
                        className="self-end"
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            </SkeletonWrapper>
          </section>
        </div>
      </div>
      {selectedDocument ? (
        <PdfPreviewDialog
          isOpen
          onClose={closeDocumentPreview}
          title={selectedDocument.documentTitle}
          subtitle={`${selectedDocument.categoryDisplayLabel} · ${selectedDocument.displayDate}`}
          pdfUrl={selectedDocumentBlobUrl}
        />
      ) : null}
    </main>
  )
}
